// ============================================================
// PLUMME — AI Rådgiver: utfall og læring
// ============================================================
// «Hva ble det faktisk av rådet?» Når brukeren merker en anbefaling som gjort,
// lagres tallet rådet gjaldt PLUSS måletallet fra regnskapet på det tidspunktet.
// Senere sammenlignes måletallet med hva det er nå, og differansen er det som
// faktisk skjedde.
//
// Det viktigste her er hva vi IKKE gjør:
//  - Vi summerer aldri på tvers av effekttyper. Frigjort likviditet, spart kostnad
//    og sikret fradrag er ulike størrelser (samme regel som effektmerkingen).
//  - Vi later ikke som alt kan måles. «Følg markedsføringseffekten» har ingen
//    målbar fasit, og da sier vi det i stedet for å finne på et tall.
const PlummeAiAdvisorOutcomes = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);

  // Hvilket tall i regnskapet som forteller om rådet virket. Anbefalinger som ikke
  // står her kan ikke måles automatisk, og skal si det.
  const METRIC_BY_REC = {
    overdueSales: 'salesOverdue',
    missingVouchers: 'missingAttachmentCount',
    vatDue: 'vatDue',
  };
  // Retningen som betyr suksess. Alle tre skal ned.
  const LOWER_IS_BETTER = new Set(['salesOverdue', 'missingAttachmentCount', 'vatDue']);

  const measurableRec = (recId) => Boolean(METRIC_BY_REC[recId]);

  // Et utfall er ikke ferdig med en gang. Regnskapet må rekke å endre seg, og en
  // faktura betalt dagen etter purringen er ikke bevis på noe annet enn flaks.
  const MIN_DAYS_BEFORE_MEASURING = 3;

  const buildOutcome = (entry, metricsNow, now = new Date()) => {
    const metric = METRIC_BY_REC[entry.recId];
    const markedAt = entry.markedAt ? new Date(entry.markedAt) : null;
    const daysSince = markedAt && !Number.isNaN(markedAt.getTime())
      ? Math.floor((new Date(now) - markedAt) / 86400000)
      : null;
    const base = {
      id: entry.id,
      recId: entry.recId,
      effectType: entry.effectType || null,
      estimateLabel: entry.estimateLabel || null,
      markedAt: entry.markedAt || null,
      daysSince,
    };
    if (!metric) return { ...base, measurable: false, reason: 'noMetric' };
    if (daysSince != null && daysSince < MIN_DAYS_BEFORE_MEASURING) {
      return { ...base, measurable: false, reason: 'tooEarly' };
    }
    const before = num(entry.metricValue, null);
    const after = num(metricsNow?.[metric], null);
    if (entry.metricValue == null || metricsNow?.[metric] == null) {
      return { ...base, measurable: false, reason: 'noData' };
    }
    const improvement = LOWER_IS_BETTER.has(metric) ? before - after : after - before;
    return {
      ...base,
      measurable: true,
      metric,
      before,
      after,
      // Positiv = det gikk riktig vei. Negativ betyr at det ble verre, og det skal
      // vises like tydelig — et råd som ikke traff er den mest nyttige tilbakemeldingen.
      improvement,
      // «Delvis» finnes med vilje: forfalte fakturaer som gikk fra 84 til 20 er ikke
      // en fiasko, men det er heller ikke en full seier.
      result: improvement <= 0 ? 'none' : after <= 0 ? 'full' : 'partial',
    };
  };

  const buildOutcomesModel = ({ entries = [], metricsNow = {}, now = new Date() }) => {
    const outcomes = safeArray(entries)
      .map((entry) => buildOutcome(entry, metricsNow, now))
      .sort((a, b) => String(b.markedAt || '').localeCompare(String(a.markedAt || '')));
    const measured = outcomes.filter((outcome) => outcome.measurable);
    // Gruppert per effekttype, aldri summert på tvers.
    const byEffect = {};
    measured.forEach((outcome) => {
      const key = outcome.effectType || 'other';
      if (!byEffect[key]) byEffect[key] = { effectType: key, count: 0, improvement: 0 };
      byEffect[key].count += 1;
      byEffect[key].improvement += outcome.improvement;
    });
    return {
      outcomes,
      byEffect: Object.values(byEffect),
      counts: {
        total: outcomes.length,
        measured: measured.length,
        unmeasurable: outcomes.length - measured.length,
        hits: measured.filter((outcome) => outcome.result !== 'none').length,
        misses: measured.filter((outcome) => outcome.result === 'none').length,
      },
    };
  };

  return { METRIC_BY_REC, LOWER_IS_BETTER, MIN_DAYS_BEFORE_MEASURING, measurableRec, buildOutcome, buildOutcomesModel };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorOutcomes = PlummeAiAdvisorOutcomes;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorOutcomes;
